<template>
  <div class="guide-signers">
    <h2>What are signers?</h2>
    <p>
      Signers are the recipients of a signing package. They are the people who
      must sign, witness, or receive copies of the documents. Every package needs
      at least one signer before it can be sent. Signers receive notifications,
      authenticate their identity, complete their assigned fields, and produce the
      legally binding signatures on the documents.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Signer types</h2>
    <p>
      Each signer is assigned a type that determines what role they play in the signing
      transaction. Choose the type that matches the signer's purpose.
    </p>

    <div class="q-my-md" style="overflow-x: auto;">
      <table class="q-table" style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Type</th>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Purpose</th>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Action required</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Signer</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Primary document signer (default)</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Must complete all assigned fields</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>CarbonCopy</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Copy recipient (no signing role)</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Receives final documents, no signing action</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>CertifiedDelivery</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Recipient who must acknowledge receipt with an audit record</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Must open and acknowledge (no field completion)</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>InPersonSigner</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Signer who completes the ceremony in person, typically on the host's device</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Signs from the host's portal session</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Approver</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Reviewer who approves or denies before signing proceeds</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Completes Approve/Decline fields; denial terminates the package</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Editor</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Participant who can modify the package before it is sent</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Edits package pre-send; no signing obligation</td>
          </tr>
        </tbody>
      </table>
    </div>
    <p>
      <code>CarbonCopy</code> recipients are notified when the package is completed and
      receive download links to the final signed documents. They do not participate in the
      signing ceremony and are not assigned any fields. If <code>type</code> is omitted on
      create, it defaults to <code>Signer</code>.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Authentication methods</h2>
    <p>
      Before a signer can view documents and complete their fields, they must
      verify their identity. The platform offers five authentication methods,
      ranging from simple email verification to government ID checks. Stronger
      methods provide higher legal assurance but consume credits from your
      subscription.
    </p>

    <h3>Email (default, free)</h3>
    <p>
      The signer receives an email with a unique, one-time signing link. Clicking the link
      proves ownership of the email address. This is the simplest method and sufficient for
      most routine documents.
    </p>
    <GuideCodeTabs :samples="samples.authEmail" />

    <h3>AccessCode (free)</h3>
    <p>
      The signer must enter a pre-shared access code before they can view the
      documents. The code is shared out-of-band, such as via phone call, a
      separate email, or in person. This adds a second factor without consuming
      any credits.
    </p>
    <GuideCodeTabs :samples="samples.authAccessCode" />

    <h3>SMS (consumes SMS credit)</h3>
    <p>
      The signer receives a one-time passcode via text message that they must enter before
      viewing documents. This proves possession of the phone number and is a strong second
      factor. Requires a valid phone number on the signer.
    </p>
    <GuideCodeTabs :samples="samples.authSms" />

    <h3>KBA (consumes KBA credit)</h3>
    <p>
      Knowledge-Based Authentication generates identity-verification questions
      from public records, for example "Which of the following addresses have you
      been associated with?" The signer must answer correctly to proceed. This is
      the strongest non-biometric verification and is often required for real
      estate, financial, and legal documents.
    </p>
    <GuideCodeTabs :samples="samples.authKba" />

    <h3>IdVerification (consumes IDV credit)</h3>
    <p>
      Government ID verification combined with a selfie match, powered by Persona.com. The
      signer photographs a government-issued ID and takes a selfie; the system verifies that
      the document is authentic and that the selfie matches the ID photo. This is the highest
      assurance level and is required for some regulatory workflows.
    </p>
    <GuideCodeTabs :samples="samples.authIdv" />

    <q-banner class="bg-amber-1 text-amber-10 q-my-md" rounded>
      <strong>Cost note:</strong> SMS, KBA, and IdVerification each consume one credit of their
      respective type from your subscription. Email and AccessCode are free. Choose the
      authentication level appropriate for your document's legal significance.
    </q-banner>

    <h3>Stacking multiple auth methods</h3>
    <p>
      The single <code>authMethod</code> field selects one method. You can also
      require a signer to pass multiple methods in sequence before they view
      documents by setting <code>requiredAuthMethods</code> to an ordered list:
    </p>
    <GuideCodeTabs :samples="samples.authStacked" />
    <p>
      Each method in the list is completed in order. The signing ceremony exposes
      the current step and total, so your UI (if embedded) can show progress.
      Credit-consuming methods (<code>Sms</code>, <code>Kba</code>,
      <code>IdVerification</code>) each consume one credit of their type per
      signer attempt, so stacking increases cost. Useful for real estate,
      financial, and other high-assurance workflows.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Routing order</h2>
    <p>
      When <code>enforceSigningOrder</code> is enabled on the package, signers are notified
      in sequence based on their <code>routingOrder</code> value rather than all at once.
    </p>
    <ul>
      <li>Signers with <code>routingOrder: 1</code> are notified first.</li>
      <li>Signers with <code>routingOrder: 2</code> are notified only after all <code>routingOrder: 1</code> signers have completed their fields.</li>
      <li>This continues through each routing-order level until all signers are complete.</li>
    </ul>
    <p>Sequential signing matters for workflows where document flow matters. For example:</p>
    <GuideCodeTabs :samples="samples.bulkAdd" />

    <q-separator class="q-my-lg" />

    <h2>Managing signers in the portal</h2>
    <p>
      The package builder has a <strong>Signers</strong> panel where you add each
      recipient's name, email, type, and authentication requirement. Drag to
      reorder signers, which updates the routing order. To reassign an in-flight
      signer, open the package detail page, expand the signer entry, and click
      <strong>Reassign</strong>. Provide the new person's email and name. To nudge
      a slow signer, click <strong>Resend</strong> on the same panel.
    </p>

    <section class="dev-section">
    <h2>For developers and enterprise integration</h2>
    <p>
      Every signer operation available in the portal has a REST endpoint and an
      MCP tool. The key distinction the API surface makes is between Update (only
      works while the package is Draft) and Reassign (only works after the
      package is Sent). Both are covered below.
    </p>

    <h3>Updating vs. reassigning a signer</h3>
    <p>
      Two different endpoints modify a signer, with different semantics depending
      on whether the package is still in Draft or has been sent.
    </p>

    <h3>Update (Draft only)</h3>
    <p>
      While the package is in <code>Draft</code>, use
      <code>PUT /signers/{signerId}</code> to change any signer field (email,
      name, auth method, routing order, and so on). No notifications are sent
      because the package has not been sent yet. Partial-update semantics apply:
      omitted fields are unchanged.
    </p>
    <GuideCodeTabs :samples="samples.updateSigner" />

    <h3>Reassign (Sent or InProgress)</h3>
    <p>
      Once the package has been sent, identity changes go through the reassign
      endpoint. This revokes the original signer's access, resets their auth
      progress (SMS OTP state, KBA attempts, and IDV status are all cleared), and
      sends a fresh notification to the new person. The signer's completed fields
      are retained. Only their identity changes.
    </p>
    <GuideCodeTabs :samples="samples.reassign" />
    <p>
      Reassignment is allowed only while the original signer is still pending. A
      signer who has already completed returns <code>409 Conflict</code>. At that
      point the only path forward is to void the package and clone it. Reassign
      does not apply to <code>Draft</code> packages. Use Update instead.
    </p>

    <q-separator class="q-my-lg" />

    <h3>Resending notifications</h3>
    <p>
      Signers who have not yet acted on their signing request can be nudged with
      a resend. The endpoint dispatches a new notification email with a fresh
      signing link. Resends are rate-limited to 10 requests per minute per tenant
      under the <code>resend</code> policy. Exceeding the limit returns
      <code>429</code>.
    </p>
    <GuideCodeTabs :samples="samples.resend" />

    <q-separator class="q-my-lg" />

    <h3>Bulk operations</h3>
    <p>
      The platform provides bulk endpoints for common signer operations to reduce
      round trips when working with packages that have many signers.
    </p>
    <ul>
      <li>
        <strong>Add multiple signers.</strong>
        <code>POST /api/v1/packages/{id}/signers/bulk</code> accepts an array of
        signers to add in a single request.
      </li>
      <li>
        <strong>Nudge multiple signers.</strong>
        <code>POST /api/v1/packages/{id}/signers/resend-bulk</code> sends
        notification reminders to multiple signers at once.
      </li>
      <li>
        <strong>Reorder routing.</strong>
        <code>PUT /api/v1/packages/{id}/signers/reorder</code> batch-updates the
        routing order for all signers in one call.
      </li>
    </ul>

    <q-separator class="q-my-lg" />

    <h3>Best practices</h3>
    <ul>
      <li>
        <strong>Match authentication to risk.</strong> Use Email authentication
        for low-risk documents like internal acknowledgements. Use SMS or KBA for
        contracts with financial implications. Reserve IdVerification for
        regulatory or high-value transactions.
      </li>
      <li>
        <strong>Set routing order when document flow matters.</strong> If the
        seller must sign before the buyer, or the employee must sign before the
        manager approves, enable sequential signing and assign appropriate
        routing orders.
      </li>
      <li>
        <strong>Include CC recipients for compliance.</strong> Adding your legal
        team, a filing system, or an archive mailbox as CC ensures the right
        people receive the completed documents automatically without
        participating in the ceremony.
      </li>
      <li>
        <strong>Use reassignment instead of voiding.</strong> If the wrong
        person was added, reassign their slot rather than voiding the entire
        package. Reassignment preserves the progress of all other signers.
      </li>
      <li>
        <strong>Prefer bulk operations for multi-signer packages.</strong> Adding
        signers one at a time works fine for two or three people. Use the bulk
        endpoint when building packages with many recipients to reduce API calls
        and latency.
      </li>
    </ul>

    <q-banner class="bg-blue-1 text-blue-9 q-mt-lg" rounded>
      <strong>Tip:</strong> When using AccessCode authentication, share the code
      through a different channel than the signing notification. For example,
      send the signing link via email and the access code via phone. That
      achieves true two-factor verification at no additional credit cost.
    </q-banner>

    <q-banner class="bg-green-1 text-green-9 q-mt-md" rounded>
      <template v-slot:avatar>
        <q-icon name="menu_book" color="green-9" />
      </template>
      See the <strong>Signers</strong> section in the
      <a href="/Docs/ApiReference" class="text-green-9" style="text-decoration: underline;">API Reference</a>
      for complete endpoint schemas and try-it-out examples.
    </q-banner>
    </section>
  </div>
</template>

<script>
import GuideCodeTabs from './shared/GuideCodeTabs.vue';

const API = 'https://api.esignlaunchpad.com';

// Helper to render a JSON-body fragment as a single-tab example. The 5 auth-method
// examples and the stacked-auth example are pure request-body fragments, not full API
// calls, so they use a single 'json' tab rather than the full 7-language set.
const jsonBody = (obj) => ({ json: JSON.stringify(obj, null, 2) });

const samples = {
    authEmail:      jsonBody({ name: 'Jane Doe', email: 'jane@example.com', authMethod: 'Email' }),
    authAccessCode: jsonBody({ name: 'Jane Doe', email: 'jane@example.com', authMethod: 'AccessCode', accessCode: 'SECURE-2026' }),
    authSms:        jsonBody({ name: 'Jane Doe', email: 'jane@example.com', authMethod: 'Sms', phone: '+15125551234' }),
    authKba:        jsonBody({ name: 'Jane Doe', email: 'jane@example.com', authMethod: 'Kba' }),
    authIdv:        jsonBody({ name: 'Jane Doe', email: 'jane@example.com', authMethod: 'IdVerification' }),
    authStacked:    jsonBody({ name: 'Jane Doe', email: 'jane@example.com', phone: '+15125551234', requiredAuthMethods: ['Sms', 'Kba'] }),

    // ─── Bulk-add signers (full API call, 7 tabs) ───────────────────────────────
    bulkAdd: {
        curl: `curl -X POST ${API}/api/v1/packages/$PACKAGE_ID/signers/bulk \\
  -H "Authorization: Bearer $ESIGN_TOKEN" -H "Content-Type: application/json" \\
  -d '{
    "signers": [
      { "name": "Alex Chen",     "email": "alex@acme.com", "type": "Signer",     "routingOrder": 1 },
      { "name": "Pat Rivera",    "email": "pat@acme.com",  "type": "Signer",     "routingOrder": 2 },
      { "name": "HR Department", "email": "hr@acme.com",   "type": "CarbonCopy", "routingOrder": 3 }
    ]
  }'`,

        js: `await fetch(\`${API}/api/v1/packages/\${packageId}/signers/bulk\`, {
  method: 'POST',
  headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
  body: JSON.stringify({
    signers: [
      { name: 'Alex Chen',     email: 'alex@acme.com', type: 'Signer',     routingOrder: 1 },
      { name: 'Pat Rivera',    email: 'pat@acme.com',  type: 'Signer',     routingOrder: 2 },
      { name: 'HR Department', email: 'hr@acme.com',   type: 'CarbonCopy', routingOrder: 3 }
    ]
  })
});`,

        node: `await fetch(\`${API}/api/v1/packages/\${packageId}/signers/bulk\`, {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    signers: [
      { name: 'Alex Chen',     email: 'alex@acme.com', type: 'Signer',     routingOrder: 1 },
      { name: 'Pat Rivera',    email: 'pat@acme.com',  type: 'Signer',     routingOrder: 2 },
      { name: 'HR Department', email: 'hr@acme.com',   type: 'CarbonCopy', routingOrder: 3 }
    ]
  })
});`,

        python: `requests.post(
    f'${API}/api/v1/packages/{package_id}/signers/bulk',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={
        'signers': [
            {'name': 'Alex Chen',     'email': 'alex@acme.com', 'type': 'Signer',     'routingOrder': 1},
            {'name': 'Pat Rivera',    'email': 'pat@acme.com',  'type': 'Signer',     'routingOrder': 2},
            {'name': 'HR Department', 'email': 'hr@acme.com',   'type': 'CarbonCopy', 'routingOrder': 3},
        ]
    }
).raise_for_status()`,

        csharp: `var signers = new object[]
{
    new { name = "Alex Chen",     email = "alex@acme.com", type = "Signer",     routingOrder = 1 },
    new { name = "Pat Rivera",    email = "pat@acme.com",  type = "Signer",     routingOrder = 2 },
    new { name = "HR Department", email = "hr@acme.com",   type = "CarbonCopy", routingOrder = 3 },
};

await client.PostAsJsonAsync(
    $"api/v1/packages/{packageId}/signers/bulk",
    new { signers });`,

        php: `$signers = [
    ['name' => 'Alex Chen',     'email' => 'alex@acme.com', 'type' => 'Signer',     'routingOrder' => 1],
    ['name' => 'Pat Rivera',    'email' => 'pat@acme.com',  'type' => 'Signer',     'routingOrder' => 2],
    ['name' => 'HR Department', 'email' => 'hr@acme.com',   'type' => 'CarbonCopy', 'routingOrder' => 3],
];

$client->post("api/v1/packages/{$packageId}/signers/bulk", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => ['signers' => $signers],
]);`,

        ruby: `signers = [
  { name: 'Alex Chen',     email: 'alex@acme.com', type: 'Signer',     routingOrder: 1 },
  { name: 'Pat Rivera',    email: 'pat@acme.com',  type: 'Signer',     routingOrder: 2 },
  { name: 'HR Department', email: 'hr@acme.com',   type: 'CarbonCopy', routingOrder: 3 }
]

uri = URI("${API}/api/v1/packages/#{package_id}/signers/bulk")
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = { signers: signers }.to_json
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }`
    },

    // ─── Update signer (Draft only) ─────────────────────────────────────────────
    updateSigner: {
        curl: `curl -X PUT ${API}/api/v1/packages/$PACKAGE_ID/signers/$SIGNER_ID \\
  -H "Authorization: Bearer $ESIGN_TOKEN" -H "Content-Type: application/json" \\
  -d '{
    "email": "correct-address@example.com",
    "authMethod": "Sms",
    "phone": "+15125551234"
  }'`,

        js: `await fetch(\`${API}/api/v1/packages/\${packageId}/signers/\${signerId}\`, {
  method: 'PUT',
  headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
  body: JSON.stringify({
    email: 'correct-address@example.com',
    authMethod: 'Sms',
    phone: '+15125551234'
  })
});`,

        node: `await fetch(\`${API}/api/v1/packages/\${packageId}/signers/\${signerId}\`, {
  method: 'PUT',
  headers: {
    'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    email: 'correct-address@example.com',
    authMethod: 'Sms',
    phone: '+15125551234'
  })
});`,

        python: `requests.put(
    f'${API}/api/v1/packages/{package_id}/signers/{signer_id}',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={
        'email': 'correct-address@example.com',
        'authMethod': 'Sms',
        'phone': '+15125551234'
    }
).raise_for_status()`,

        csharp: `await client.PutAsJsonAsync(
    $"api/v1/packages/{packageId}/signers/{signerId}",
    new
    {
        email = "correct-address@example.com",
        authMethod = "Sms",
        phone = "+15125551234"
    });`,

        php: `$client->put("api/v1/packages/{$packageId}/signers/{$signerId}", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => [
        'email' => 'correct-address@example.com',
        'authMethod' => 'Sms',
        'phone' => '+15125551234',
    ],
]);`,

        ruby: `uri = URI("${API}/api/v1/packages/#{package_id}/signers/#{signer_id}")
req = Net::HTTP::Put.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = {
  email: 'correct-address@example.com',
  authMethod: 'Sms',
  phone: '+15125551234'
}.to_json
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }`
    },

    // ─── Reassign signer (Sent / InProgress only) ───────────────────────────────
    reassign: {
        curl: `curl -X POST ${API}/api/v1/packages/$PACKAGE_ID/signers/$SIGNER_ID/reassign \\
  -H "Authorization: Bearer $ESIGN_TOKEN" -H "Content-Type: application/json" \\
  -d '{
    "name": "New Person",
    "email": "newperson@example.com",
    "phone": "+15125551234"
  }'`,

        js: `await fetch(
  \`${API}/api/v1/packages/\${packageId}/signers/\${signerId}/reassign\`,
  {
    method: 'POST',
    headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: 'New Person',
      email: 'newperson@example.com',
      phone: '+15125551234'
    })
  }
);`,

        node: `await fetch(
  \`${API}/api/v1/packages/\${packageId}/signers/\${signerId}/reassign\`,
  {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      name: 'New Person',
      email: 'newperson@example.com',
      phone: '+15125551234'
    })
  }
);`,

        python: `requests.post(
    f'${API}/api/v1/packages/{package_id}/signers/{signer_id}/reassign',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={
        'name': 'New Person',
        'email': 'newperson@example.com',
        'phone': '+15125551234'
    }
).raise_for_status()`,

        csharp: `await client.PostAsJsonAsync(
    $"api/v1/packages/{packageId}/signers/{signerId}/reassign",
    new
    {
        name = "New Person",
        email = "newperson@example.com",
        phone = "+15125551234"
    });`,

        php: `$client->post("api/v1/packages/{$packageId}/signers/{$signerId}/reassign", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => [
        'name' => 'New Person',
        'email' => 'newperson@example.com',
        'phone' => '+15125551234',
    ],
]);`,

        ruby: `uri = URI("${API}/api/v1/packages/#{package_id}/signers/#{signer_id}/reassign")
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = {
  name: 'New Person',
  email: 'newperson@example.com',
  phone: '+15125551234'
}.to_json
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }`
    },

    // ─── Resend notification (rate-limited 10/min) ──────────────────────────────
    resend: {
        curl: `curl -X POST ${API}/api/v1/packages/$PACKAGE_ID/signers/$SIGNER_ID/resend \\
  -H "Authorization: Bearer $ESIGN_TOKEN"`,

        js: `await fetch(
  \`${API}/api/v1/packages/\${packageId}/signers/\${signerId}/resend\`,
  { method: 'POST', headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN } }
);`,

        node: `const response = await fetch(
  \`${API}/api/v1/packages/\${packageId}/signers/\${signerId}/resend\`,
  { method: 'POST', headers: { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN } }
);
if (response.status === 429) {
  console.warn('Rate limited - retry after', response.headers.get('Retry-After'), 'seconds');
}`,

        python: `response = requests.post(
    f'${API}/api/v1/packages/{package_id}/signers/{signer_id}/resend',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"}
)
if response.status_code == 429:
    print('Rate limited - retry after', response.headers['Retry-After'], 'seconds')
else:
    response.raise_for_status()`,

        csharp: `var response = await client.PostAsync(
    $"api/v1/packages/{packageId}/signers/{signerId}/resend", null);

if ((int)response.StatusCode == 429)
{
    var retryAfter = response.Headers.RetryAfter?.Delta;
    Console.WriteLine($"Rate limited - retry after {retryAfter}");
}
else
{
    response.EnsureSuccessStatusCode();
}`,

        php: `try {
    $client->post("api/v1/packages/{$packageId}/signers/{$signerId}/resend", [
        'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    ]);
} catch (\\GuzzleHttp\\Exception\\ClientException $e) {
    if ($e->getResponse()->getStatusCode() === 429) {
        $retry = $e->getResponse()->getHeader('Retry-After')[0] ?? null;
        echo "Rate limited - retry after {$retry} seconds\\n";
    } else { throw $e; }
}`,

        ruby: `uri = URI("${API}/api/v1/packages/#{package_id}/signers/#{signer_id}/resend")
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }

if res.code == '429'
  puts "Rate limited - retry after #{res['Retry-After']} seconds"
end`
    }
};

export default {
    name: 'GuideSigners',
    components: { GuideCodeTabs },
    data() { return { samples }; }
};
</script>

